import { z } from "zod";

export const welcomeFormSchema = z.object({
  gender: z.enum(
    ["Male", "Female", "Non-binary", "Other", "None of the above"],
    {
      message: "Please select a gender",
    },
  ),
  events: z
    .array(
      z.enum(["Wedding", "Vacation", "Reunion", "Health Reasons", "Other"], {
        message: "Please select an event",
      }),
    )
    .min(1, "Please select at least one event"),
  glpIntake: z.enum(["Yes", "No"], {
    message: "Please select a GLP intake",
  }),
  otherGender: z
    .string()
    .optional()
    .transform((v) => v || undefined)
    .refine((val) => {
      if (val === "other") {
        return val && val.length > 0;
      }
      return true;
    }, "Please specify your gender"),
  otherEvents: z
    .string()
    .optional()
    .transform((v) => v || undefined)
    .refine((val) => {
      if (val === "other") {
        return val && val.length > 0;
      }
      return true;
    }, "Please give a reason"),
  weight: z
    .string()
    .min(1, "Weight is required")
    .regex(/^\d+(\.\d+)?$/, "Must be a valid number")
    .transform((val) => parseFloat(val))
    .refine(
      (val) => val > 0 && val < 500,
      "Weight must be between 0 and 500 lbs",
    ),
  height: z
    .string()
    .min(1, "Height is required")
    .refine(
      (val) => {
        // Allow either feet'inches' format or just numbers
        return /^\d+'?\d*'?$/.test(val) || /^\d+$/.test(val);
      },
      "Must be in feet'inches' format (e.g. 5'11') or just numbers"
    ),
  targetWeight: z
    .string()
    .min(1, "Target Weight is required")
    .regex(/^\d+(\.\d+)?$/, "Must be a valid number")
    .transform((val) => parseFloat(val))
    .refine(
      (val) => val > 0 && val < 500,
      "Weight must be between 0 and 500 lbs",
    ),
  timeframe: z
    .string()
    .min(1, "Time Frame is required"),
  recentdose: z
    .string()
    .min(1, "Recent Dose is required"),
  lastintake: z
    .string()
    .min(1, "Last Intake is required"),
  onMedication: z.enum(["Yes", "No"], {
    message: "Please select a medication",
  }),
  medication: z.enum(["Yes", "No"], {
    message: "Please select a medication",
  }),
  medications: z
    .array(z.string())
    .optional()
    .transform((val) => val || []),
  otherMedication: z
    .string()
    .optional()
    .transform((val) => val || ""),
  history: z.enum(["Yes", "No"], {
    message: "Please provide a response",
  }),
  otherHistory: z
    .string()
    .optional()
    .refine((val) => {
      if (val === "Other") {
        return val && val.length > 0;
      }
      return true;
    }, "Please specify your reason"),
  healthCondition: z
    .array(
      z.enum(
        [
          "MEN1 (Multiple Endocrine Neoplasia Type 1)",
          "MEN2 (Multiple Endocrine Neoplasia Type 2)",
          "Thyroid Cancer",
          "Pancreatic Cancer",
          "None of the above",
        ],
        {
          message: "Please select an event",
        },
      ),
    )
    .min(1, "Please select at least one event")
    .refine(
      (values) => {
        if (values.includes("None of the above")) {
          return values.length === 1;
        }
        return !values.includes("None of the above");
      },
      {
        message: '"None of the above" cannot be selected with other conditions',
      },
    ),
});

export type WelcomeFormSchema = z.infer<typeof welcomeFormSchema>;
export type WelcomeFormFieldName = keyof WelcomeFormSchema;
